import { getBYOKAuthErrorSection } from "src/aiNode/byokAuthError";
import { ErrorSection } from "src/base-components/MultipleErrorCallout";

// Trailing connect error JSON (shared by tryExtractProviderRawResponse and formatConnectErrorMessage).
// - (content=|Details: ) - Match either "content=" or "Details: ".
// - (b?['"]?) - Match an optional "b" followed by an optional single or double quote.
// - ({[\s\S]+}) - Match a JSON object.
// - (['"]?)$ - Match an optional single or double quote at the end of the string.
const CONNECT_TRAILING_JSON_RE =
  /(content=|Details: )(b?['"]?)({[\s\S]+})(['"]?)$/;

/**
 * Extracts `detail[0].ctx.provider_raw_response` from a trailing connect JSON
 * payload, if present. When the raw response itself is valid JSON, it is
 * pretty-printed; otherwise the original string is returned as-is.
 */
export const tryExtractProviderRawResponse = (msg: string): string | null => {
  const m = msg.match(CONNECT_TRAILING_JSON_RE);
  if (!m?.[3]) {
    return null;
  }
  try {
    const o = JSON.parse(m[3]) as {
      detail?: { ctx?: { provider_raw_response?: unknown } }[];
    };
    const raw = o.detail?.[0]?.ctx?.provider_raw_response;
    if (typeof raw !== "string") {
      return null;
    }
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  } catch {
    return null;
  }
};

/**
 * Extracts the JSON content from a connect error message and formats it.
 * @param msg The connect error message. Expected format: "... content={...}" or "... Details: {...}".
 * @returns The connect error message with the JSON content formatted. If the JSON content cannot be
 * parsed, the original message is returned.
 */
export const formatConnectErrorMessage = (msg: string) => {
  const match = msg.match(CONNECT_TRAILING_JSON_RE);
  if (!match) {
    return msg;
  }

  // There are at least 5 match groups: the full match and the 4 groups in the regex.
  if (match.length < 5) {
    return msg;
  }

  const jsonContent = match[3];
  try {
    const jsonObject = JSON.parse(jsonContent);
    const formattedJson = JSON.stringify(jsonObject, null, 2);
    // Replace the original content (full matched string - match[0]) with the formatted JSON
    // preceded by the original content prefix (match[1]).
    return msg.replace(match[0], `${match[1]}${formattedJson}`);
  } catch {
    return msg;
  }
};

/**
 * Returns true when `s` contains a JSON object/array. We skip any leading
 * non-JSON text up to the first `{` or `[`, then attempt to parse the
 * remainder.
 */
export const containsJsonBlob = (s: string): boolean => {
  const start = s.search(/[{[]/);
  if (start === -1) {
    return false;
  }
  try {
    JSON.parse(s.slice(start));
    return true;
  } catch {
    return false;
  }
};

/**
 * Returns the formatted messages to display in the UI as an array of
 * sections.
 */
export const getFormattedErrorDisplayMessages = (
  msg: string,
): ErrorSection[] => {
  const sections: ErrorSection[] = [];
  // Surface a friendly explanation above the raw blob for BYOK auth failures.
  const byokSection = getBYOKAuthErrorSection(msg);
  if (byokSection) {
    sections.push(byokSection);
  }
  const providerResponse = tryExtractProviderRawResponse(msg);
  if (providerResponse) {
    sections.push({
      title: "Provider response:",
      errorBlob: providerResponse,
      isCodeBlock: true,
    });
  }
  const fullErrorBlob = formatConnectErrorMessage(msg);
  sections.push({
    title: "Full error message:",
    errorBlob: fullErrorBlob,
    isCodeBlock: containsJsonBlob(fullErrorBlob),
  });
  return sections;
};
